/** 位置情報の取得（仕様書 §11）。 */

import type { EntryLocation } from '../domain/types';
import { nowIsoUtc } from './datetime';

export class LocationError extends Error {
  readonly kind: 'denied' | 'timeout' | 'unavailable';

  constructor(kind: 'denied' | 'timeout' | 'unavailable', message: string) {
    super(message);
    this.name = 'LocationError';
    this.kind = kind;
  }
}

/** HTTPS でないと Geolocation は使えない。ボタン自体を出さない判断に使う。 */
export function geolocationAvailable(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator && window.isSecureContext;
}

/** 端末の現在地。 */
export function getDeviceLocation(): Promise<EntryLocation> {
  return new Promise((resolve, reject) => {
    if (!geolocationAvailable()) {
      reject(new LocationError('unavailable', 'この環境では位置情報を取得できません。'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
          source: 'device',
          capturedAt: nowIsoUtc(),
        }),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          reject(
            new LocationError(
              'denied',
              '位置情報の利用が許可されていません。ブラウザの設定をご確認ください。',
            ),
          );
        } else if (err.code === err.TIMEOUT) {
          reject(new LocationError('timeout', '現在地を取得できませんでした。もう一度お試しください。'));
        } else {
          reject(new LocationError('unavailable', '現在地を取得できませんでした。'));
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  });
}

export function exifLocation(gps: { lat: number; lng: number }): EntryLocation {
  return { lat: gps.lat, lng: gps.lng, accuracy: null, source: 'exif', capturedAt: nowIsoUtc() };
}

/** 外部地図アプリへのリンク。Apple 系は Apple マップ、それ以外は Google マップ。 */
export function mapUrl(loc: { lat: number; lng: number }): string {
  const isApple = /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? navigator.userAgent);
  return isApple
    ? `https://maps.apple.com/?ll=${loc.lat},${loc.lng}`
    : `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
}

export function formatLatLng(loc: { lat: number; lng: number }): string {
  return `${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}`;
}
